import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Star, Shield, Clock, Users, ChevronRight, Heart, Phone, MessageCircle } from 'lucide-react';
import AuthModal from '../components/AuthModal';

const LandingPage = ({ onEnter }) => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const navigate = useNavigate();

  // Scroll to top au chargement
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleStart = () => {
    localStorage.setItem('nb_landing_seen', 'true');
    localStorage.setItem('nb_seen_landing', 'true');
    if (onEnter) onEnter();
  };

  const goToMentions = () => {
    navigate('/mentions-legales');
  };

  const goToConfidentialite = () => {
    navigate('/confidentialite');
  };

  const goToCGV = () => {
    navigate('/cgv');
  };

  const handleSignup = () => {
    setShowAuthModal(true);
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    handleStart();
  };

  const testimonials = [
    {
      name: "Catherine",
      age: 54,
      city: "Toulouse",
      text: "J'ai enfin trouvé quelqu'un qui me comprend. Louis m'a aidée à perdre 7 kilos en 4 mois, sans me priver. Je recommande à toutes mes amies !",
      result: "-7 kg",
      initials: "C"
    },
    {
      name: "Jean-Pierre",
      age: 58,
      city: "Marseille", 
      text: "À mon âge, je pensais que c'était trop tard. Louis m'a prouvé le contraire avec des conseils simples et adaptés à ma vie quotidienne.",
      result: "-9 kg",
      initials: "JP"
    },
    {
      name: "Nathalie",
      age: 47,
      city: "Rennes",
      text: "Les plans de repas sont vraiment pratiques. Je sais enfin quoi cuisiner pour toute la famille, c'est équilibré et tout le monde aime !",
      result: "-5 kg",
      initials: "N"
    }
  ];

  return (
    <div className="landing-page">
      {/* Header avec logo */}
      <header className="landing-header">
        <div className="landing-logo">
          <img src="./robot-icon.svg" alt="MonCoach" />
          <span>MonCoach</span>
        </div>
        <button className="header-login" onClick={() => setShowAuthModal(true)}>
          Connexion
        </button>
      </header>

      {/* Hero Section - Simple et direct */}
      <section className="landing-hero">
        <div className="hero-content">
          <div className="trust-badge-main">
            <Shield size={16} />
            <span>Plus de 3 000 personnes accompagnées</span>
          </div>

          <h1>Perdez du poids<br />sans régime compliqué</h1>
          
          <p className="hero-subtitle">
            <strong>Louis</strong>, votre coach nutrition personnel, vous accompagne 
            chaque jour avec des conseils <strong>simples</strong> et <strong>adaptés à votre vie</strong>.
          </p>

          <div className="hero-benefits">
            <div className="hero-benefit">
              <Check size={20} />
              <span>Des menus faciles à suivre</span>
            </div>
            <div className="hero-benefit">
              <Check size={20} />
              <span>Des conseils personnalisés</span>
            </div>
            <div className="hero-benefit">
              <Check size={20} />
              <span>Un suivi bienveillant</span>
            </div>
          </div>

          <div className="hero-buttons">
            <button className="btn-primary-large" onClick={handleSignup}>
              Commencer gratuitement
              <ChevronRight size={20} />
            </button>
          </div>

          <p className="hero-reassurance">
            <Shield size={14} /> Gratuit pour démarrer • Sans engagement • Sans carte bancaire
          </p>
        </div>

        <div className="hero-visual">
          <div className="coach-card">
            <div className="coach-photo">
              <img src="./robot-icon.svg" alt="Louis" />
            </div>
            <div className="coach-info">
              <h3>Louis</h3>
              <p>Votre coach nutrition</p>
              <div className="coach-status">
                <span className="status-dot"></span>
                Disponible maintenant
              </div>
            </div>
            <div className="coach-message">
              <p>"Bonjour ! Je suis là pour vous aider à retrouver la forme, à votre rythme. On commence ?"</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bandeau de confiance */}
      <section className="trust-bar">
        <div className="trust-item">
          <Users size={24} />
          <div>
            <strong>3 247</strong>
            <span>personnes accompagnées</span>
          </div>
        </div>
        <div className="trust-item">
          <Star size={24} />
          <div>
            <strong>4,8/5</strong>
            <span>de satisfaction</span>
          </div>
        </div>
        <div className="trust-item">
          <Heart size={24} />
          <div>
            <strong>-5 kg</strong>
            <span>en moyenne après 2 mois</span>
          </div>
        </div>
      </section>

      {/* Comment ça marche - Ultra simple */}
      <section className="how-section">
        <h2>Comment ça fonctionne ?</h2>
        <p className="section-desc">C'est simple comme bonjour</p>
        
        <div className="steps-simple">
          <div className="step-simple">
            <div className="step-icon">💬</div>
            <h3>1. Discutez avec Louis</h3>
            <p>Expliquez-lui vos objectifs, vos contraintes, ce que vous aimez manger. Il s'adapte à vous.</p>
          </div>
          
          <div className="step-simple">
            <div className="step-icon">📋</div>
            <h3>2. Recevez votre plan</h3>
            <p>Des menus pour la semaine, des recettes simples, et une liste de courses prête à l'emploi.</p>
          </div>
          
          <div className="step-simple">
            <div className="step-icon">📈</div>
            <h3>3. Suivez vos progrès</h3>
            <p>Louis vous accompagne jour après jour. Posez-lui vos questions, il est toujours là.</p>
          </div>
        </div>
      </section>

      {/* Ce que vous obtenez */}
      <section className="features-section">
        <h2>Ce que vous allez recevoir</h2>
        
        <div className="features-grid">
          <div className="feature-item">
            <MessageCircle size={32} />
            <h3>Un coach à votre écoute</h3>
            <p>Posez vos questions à Louis quand vous voulez. Il répond de manière claire et bienveillante.</p>
          </div>
          
          <div className="feature-item">
            <div className="feature-icon-emoji">🍽️</div>
            <h3>Des menus personnalisés</h3>
            <p>Un programme sur 7 jours adapté à vos goûts, avec des recettes que toute la famille peut apprécier.</p>
          </div>
          
          <div className="feature-item">
            <div className="feature-icon-emoji">🛒</div>
            <h3>Liste de courses incluse</h3>
            <p>Plus besoin de vous casser la tête. Imprimez la liste et faites vos courses sereinement.</p>
          </div>
          
          <div className="feature-item">
            <div className="feature-icon-emoji">📊</div>
            <h3>Suivi de vos repas</h3>
            <p>Notez ce que vous mangez, Louis calcule les calories pour vous. C'est simple et rapide.</p>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="testimonials-section">
        <h2>Ils ont retrouvé leur équilibre</h2>
        <p className="section-desc">Des résultats concrets, des personnes comme vous</p>
        
        <div className="testimonials-list">
          {testimonials.map((t, i) => (
            <div key={i} className="testimonial-item">
              <div className="testimonial-top">
                <div className="testimonial-avatar">{t.initials}</div>
                <div className="testimonial-meta">
                  <strong>{t.name}, {t.age} ans</strong>
                  <span>{t.city}</span>
                </div>
                <div className="testimonial-result">{t.result}</div>
              </div>
              <p>"{t.text}"</p>
              <div className="testimonial-stars">
                <Star size={16} fill="#f59e0b" color="#f59e0b" />
                <Star size={16} fill="#f59e0b" color="#f59e0b" />
                <Star size={16} fill="#f59e0b" color="#f59e0b" />
                <Star size={16} fill="#f59e0b" color="#f59e0b" />
                <Star size={16} fill="#f59e0b" color="#f59e0b" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ rapide */}
      <section className="faq-section">
        <h2>Questions fréquentes</h2>
        
        <div className="faq-list">
          <div className="faq-item">
            <h4>Est-ce vraiment gratuit pour commencer ?</h4>
            <p>Oui, l'inscription est 100% gratuite. Vous pouvez discuter avec Louis et tester les fonctionnalités sans rien payer.</p>
          </div>
          <div className="faq-item">
            <h4>Je ne suis pas doué(e) avec les ordinateurs, c'est compliqué ?</h4>
            <p>Pas du tout ! C'est aussi simple que d'envoyer un message. Vous écrivez à Louis comme à un ami, il vous répond.</p>
          </div>
          <div className="faq-item">
            <h4>Les régimes ne marchent jamais sur moi...</h4>
            <p>Justement, ce n'est pas un régime ! Louis vous aide à mieux manger progressivement, sans frustration ni privation.</p>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="final-cta-section">
        <div className="final-cta-content">
          <h2>Prêt(e) à reprendre le contrôle ?</h2>
          <p>Rejoignez les milliers de personnes qui ont changé leurs habitudes avec Louis</p>
          
          <button className="btn-primary-large white" onClick={handleSignup}>
            Créer mon compte gratuit
            <ChevronRight size={20} />
          </button>
          
          <div className="final-reassurance">
            <span><Shield size={16} /> Données sécurisées</span>
            <span><Clock size={16} /> Inscription en 1 minute</span>
            <span><Heart size={16} /> Sans engagement</span>
          </div>
        </div>
      </section>

      {/* Footer simple */}
      <footer className="landing-footer">
        <div className="footer-logo">
          <img src="./robot-icon.svg" alt="MonCoach" />
          <span>MonCoach</span>
        </div>
        <p>© 2025 MonCoach - Votre coach nutrition personnel</p>
        <div className="footer-links">
          <button onClick={handleStart}>Accéder au site</button>
          <span className="footer-separator">•</span>
          <button onClick={goToMentions}>Mentions légales</button>
          <span className="footer-separator">•</span>
          <button onClick={goToConfidentialite}>Confidentialité</button>
          <span className="footer-separator">•</span>
          <button onClick={goToCGV}>CGV</button>
        </div>
      </footer>

      {showAuthModal && (
        <AuthModal 
          onClose={() => setShowAuthModal(false)} 
          onSuccess={handleAuthSuccess}
        />
      )}
    </div>
  );
};

export default LandingPage;
